import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertColor,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Slide,
  Snackbar,
  Box,
} from '@mui/material';

type ToastSeverity = AlertColor;

interface Toast {
  id: number;
  message: string;
  severity: ToastSeverity;
  duration: number;
  action?: { label: string; onClick: () => void };
}

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface UIContextValue {
  toast: (message: string, severity?: ToastSeverity, opts?: { duration?: number; action?: Toast['action'] }) => void;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  startRouteProgress: () => void;
  stopRouteProgress: () => void;
}

const UIContext = createContext<UIContextValue | null>(null);

let nextToastId = 1;

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<
    (ConfirmOptions & { resolve: (v: boolean) => void; open: boolean }) | null
  >(null);
  const [routeBusy, setRouteBusy] = useState(0);

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback<UIContextValue['toast']>((message, severity = 'info', opts) => {
    const t: Toast = {
      id: nextToastId++,
      message,
      severity,
      duration: opts?.duration ?? (severity === 'error' ? 6000 : 3500),
      action: opts?.action,
    };
    setToasts(prev => [...prev, t]);
  }, []);

  const confirm = useCallback<UIContextValue['confirm']>((opts) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...opts, open: true, resolve });
    });
  }, []);

  const handleConfirmClose = (result: boolean) => {
    if (!confirmState) return;
    confirmState.resolve(result);
    setConfirmState({ ...confirmState, open: false });
    setTimeout(() => setConfirmState(null), 200);
  };

  const startRouteProgress = useCallback(() => setRouteBusy(b => b + 1), []);
  const stopRouteProgress = useCallback(() => setRouteBusy(b => Math.max(0, b - 1)), []);

  // Bridge axios-dispatched progress events into the counter
  useEffect(() => {
    const onStart = () => setRouteBusy(b => b + 1);
    const onStop = () => setRouteBusy(b => Math.max(0, b - 1));
    window.addEventListener('wardrobe:progress-start', onStart);
    window.addEventListener('wardrobe:progress-stop', onStop);
    return () => {
      window.removeEventListener('wardrobe:progress-start', onStart);
      window.removeEventListener('wardrobe:progress-stop', onStop);
    };
  }, []);

  const value = useMemo<UIContextValue>(
    () => ({ toast, confirm, startRouteProgress, stopRouteProgress }),
    [toast, confirm, startRouteProgress, stopRouteProgress]
  );

  return (
    <UIContext.Provider value={value}>
      {routeBusy > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            pointerEvents: 'none',
          }}
        >
          <LinearProgress
            sx={{
              height: 2,
              backgroundColor: 'transparent',
              '& .MuiLinearProgress-bar': { backgroundColor: 'secondary.main' },
            }}
          />
        </Box>
      )}
      {children}
      {toasts.map((t, idx) => (
        <Snackbar
          key={t.id}
          open
          autoHideDuration={t.duration}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            dismissToast(t.id);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          TransitionComponent={Slide}
          sx={{ mb: idx * 7 }}
        >
          <Alert
            severity={t.severity}
            variant="filled"
            onClose={() => dismissToast(t.id)}
            action={
              t.action ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    t.action!.onClick();
                    dismissToast(t.id);
                  }}
                >
                  {t.action.label}
                </Button>
              ) : undefined
            }
            sx={{ minWidth: 280, alignItems: 'center' }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
      <Dialog
        open={Boolean(confirmState?.open)}
        onClose={() => handleConfirmClose(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmState?.title || 'Are you sure?'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {confirmState?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)}>
            {confirmState?.cancelLabel || 'Cancel'}
          </Button>
          <Button
            onClick={() => handleConfirmClose(true)}
            variant="contained"
            color={confirmState?.destructive ? 'error' : 'primary'}
            autoFocus
          >
            {confirmState?.confirmLabel || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextValue => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used inside <UIProvider>');
  return ctx;
};
